import type { SimulatorField } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import type React from 'react'
import { useCallback, useState } from 'react'

import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { useProjectT } from 'services/ProjectLabels'
import { AppMode, PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'

import { useSimulatorContext } from '../SimulatorContext'
import InputFieldStyled from '../styled/InputFieldStyled'
import * as s from './InputField.module.scss'
import InputFieldToolbar from './InputFieldToolbar'

export type { SimulatorField }

interface IInputFieldProps {
  field: SimulatorField
  selected: boolean
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void
  onSelect: () => void
  onUpdateField: (values: Partial<SimulatorField>) => void
  onValueChange?: (value: string) => void
  children?: React.ReactNode
}

const InputField: React.FC<IInputFieldProps> = ({
  field,
  selected,
  onMouseDown,
  onSelect,
  onUpdateField,
  onValueChange,
  children,
}) => {
  const mode = useSimulatorContext((c) => c.mode)
  const styles = useSimulatorContext((c) => c.styles)
  const font = useSimulatorContext((c) => c.font)
  const onDeleteField = useSimulatorContext((c) => c.onDeleteField)
  const contextOnFieldValueChange = useSimulatorContext((c) => c.onFieldValueChange)
  const validateFieldOnBlur = useSimulatorContext((c) => c.validateFieldOnBlur)
  const elementId = useSimulatorContext((c) => c.elementId)
  const invalidFields = useSimulatorContext((c) => c.invalidFields)
  const error = invalidFields.find((f) => f.id === field.id)
  const [inputValue, setInputValue] = useState(field.value || '')
  const pt = useProjectT()

  const isEditor = mode.previewMode === PreviewMode.editor
  const isFill = isEditor && mode.editorMode === AppMode.fill

  const hasError = !isEditor && error !== undefined
  const fieldDomId = `${elementId}-${field.id}`
  const errorId = `field-error-${fieldDomId}`

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value)
    onValueChange?.(e.target.value)
    if (!isEditor) {
      contextOnFieldValueChange(field.id, e.target.value)
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isFill) {
      e.stopPropagation()
      onSelect()
    }
  }

  const handleToolbarMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isFill && selected) {
      e.preventDefault()
      e.stopPropagation()
      onMouseDown(e)
    }
  }

  const handleDelete = useCallback(() => {
    onDeleteField(field.id)
  }, [field.id, onDeleteField])

  const fieldWidth = typeof field.width === 'number' ? `${field.width}%` : field.width
  const fieldHeight = typeof field.height === 'number' ? `${field.height}%` : field.height

  return (
    <div
      className={cn(s.root, {
        [s.selected]: selected,
        [s.disabled]: !isFill && isEditor,
      })}
      onClick={handleClick}
      style={{
        left: `${field.x}%`,
        top: `${field.y}%`,
        width: fieldWidth,
        height: fieldHeight,
        ...font.base,
      }}
      data-simulator-field
    >
      {selected && isFill && (
        <div className={s.badge} onMouseDown={handleToolbarMouseDown}>
          <Icon name='drag' size={KitSize.XS} />
          {t('elements.simulator.textArea')}
        </div>
      )}
      <InputFieldStyled
        $font={font}
        $ghost={field.ghost}
        $hasError={hasError}
        $styles={styles}
        aria-describedby={errorId}
        aria-invalid={hasError}
        aria-label={field.placeholderValue || pt('elements.testKeyboardInput.placeholder')}
        id={fieldDomId}
        onBlur={!isEditor ? () => validateFieldOnBlur(field.id) : undefined}
        onChange={handleInputChange}
        placeholder={
          field.placeholder !== false
            ? field.placeholderValue || pt('elements.testKeyboardInput.placeholder')
            : undefined
        }
        value={inputValue}
      />
      <ScreenReaderOnlyText id={errorId} role='alert' hidden>
        {error?.message}
      </ScreenReaderOnlyText>
      {children}
      {selected && isFill && (
        <InputFieldToolbar
          field={field}
          onDeleteField={handleDelete}
          onUpdateField={onUpdateField}
        />
      )}
    </div>
  )
}

export default InputField
